import { inject, observer } from 'mobx-react'
import * as React from 'react'
import {
  presets,
  spring,
  TransitionMotion,
  TransitionStyle,
} from 'react-motion'
import * as ReactTooltip from 'react-tooltip'
import { CARD_MOVEMENT_DISTANCE, CARD_ROTATION_AMOUNT } from '../constants'
import { GASendEvent } from '../ga'
import { PlayerState } from '../models/player'
import { IRootStore } from '../models/root'
import Card from './Card'
import { IconName } from './Icon'
import Overlay from './Overlay'
import SecondaryButton from './SecondaryButton'

export interface InterpolatedStyles {
  opacity?: number
  translateX?: number
  translateY?: number
  rotation?: number
}

interface ICardStackProps {
  images: Map<string, string>
}

interface InjectedProps extends ICardStackProps {
  store: IRootStore
}

interface IVector {
  x: number
  y: number
}

interface IState {
  dragStartTime: number
  pos: IVector
  startPos: IVector
  vel: IVector
}

@inject('store')
@observer
class CardStack extends React.Component<ICardStackProps, IState> {
  public state: IState = {
    dragStartTime: 0,
    pos: { x: 0, y: 0 },
    startPos: { x: 0, y: 0 },
    vel: { x: 0, y: 0 },
  }

  get injected() {
    return this.props as InjectedProps
  }

  public componentDidMount() {
    window.addEventListener('touchmove', this.handleTouchMove, {
      passive: false,
    })
    window.addEventListener('touchend', this.handleTouchEnd)
    window.addEventListener('mousemove', this.handleMouseMove, {
      passive: false,
    })
    window.addEventListener('mouseup', this.handleMouseUp)
  }

  public componentWillUnmount() {
    window.removeEventListener('touchmove', this.handleTouchMove)
    window.removeEventListener('touchend', this.handleTouchEnd)
    window.removeEventListener('mousemove', this.handleMouseMove)
    window.removeEventListener('mouseup', this.handleMouseUp)
  }

  public render() {
    const { store } = this.injected
    const { images } = this.props

    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)

    return (
      <>
        <TransitionMotion
          defaultStyles={this.getDefaultStyles()}
          willEnter={this.willEnterHandler}
          styles={this.getStyles()}
          willLeave={this.willLeaveHandler}
        >
          {styles => (
            <div className="cardstack">
              {styles.map((style, i) => {
                return (
                  <div
                    key={style.key}
                    className="card__wrapper"
                    style={{
                      zIndex: 999 - i,
                    }}
                  >
                    <Card
                      visibleIndex={i}
                      card={style.data.card}
                      interpolatedStyles={style.style}
                      images={images}
                    />
                    <div
                      className="card__toucharea"
                      onMouseDown={this.handleMouseDown}
                      onTouchStart={this.handleTouchStart}
                    />
                  </div>
                )
              })}
            </div>
          )}
        </TransitionMotion>
        <div className="cardstack__controls">
          <ReactTooltip
            className="cardstack__tooltip"
            place="bottom"
            aria-haspopup="true"
            disable={isMobile}
          />
          <SecondaryButton
            className="bottomlink"
            iconName={IconName.prev}
            callback={this.handlePrevClick}
          />
          <SecondaryButton
            className="bottomlink"
            iconName={IconName.x}
            text="End"
            callback={this.handleEndClick}
          />
          <SecondaryButton
            className="bottomlink"
            iconName={IconName.skip}
            callback={this.handleNextClick}
          />
        </div>
        {/*
        <Overlay visible={false} closeCallback={null}>
          <h1>End your session?</h1>
          <p>You'll lose your progress.</p>
          <div className="modal__buttons">
            <SecondaryButton
              type="destructive"
              iconName={IconName.x}
              text="End"
              to="/"
            />
          </div>
        </Overlay> */}
      </>
    )
  }

  private getStringHash(input: string) {
    let hash = 0
    let chr

    if (input.length === 0) {
      return hash
    }
    for (let i = 0; i < input.length; i++) {
      chr = input.charCodeAt(i)
      // tslint:disable-next-line:no-bitwise
      hash = (hash << 5) - hash + chr
      // tslint:disable-next-line:no-bitwise
      hash |= 0 // Convert to 32bit integer
    }
    return hash
  }

  private getPseudoRandomRotation(input: string) {
    const hash = this.getStringHash(input)
    const x = Math.sin(hash) * 10000
    const random = x - Math.floor(x)
    const randomRotation = Math.floor((random - 0.5) * CARD_ROTATION_AMOUNT)

    return randomRotation
  }

  private getDefaultStyles = () => {
    const { store } = this.injected
    const visibleCards = store.visibleCards

    return visibleCards.map((visibleCard, i) => {
      return {
        data: {
          card: visibleCard,
        },
        key: 'card-' + visibleCard.title,
        style: {
          opacity: 0,
          rotation: Math.floor((Math.random() - 0.5) * CARD_ROTATION_AMOUNT),
          translateX: Math.floor(
            (Math.random() - 0.5) * CARD_MOVEMENT_DISTANCE
          ),
          translateY: Math.floor(
            (Math.random() - 0.5) * CARD_MOVEMENT_DISTANCE
          ),
        },
      }
    })
  }

  private getStyles = () => {
    const { store } = this.injected
    const { dragStartTime, pos, startPos } = this.state
    const visibleCards = store.visibleCards

    return visibleCards.map((visibleCard, i) => {
      let x = 0
      let y = 0
      let distanceRotation = 0
      let o = 1

      if (dragStartTime !== 0) {
        const delta = {
          x: pos.x - startPos.x,
          y: pos.y - startPos.y,
        }

        x = delta.x
        y = delta.y

        const sign = Math.sign(delta.x)
        const distance = Math.sqrt(delta.x * delta.x + delta.y * delta.y)
        distanceRotation = sign * distance / 100 * 5

        o = 1 - distance / 100
      }

      const opacity =
        i === 0 ? o : Number(Math.max(0, 1 - i * 0.4).toPrecision(2))
      const rotation =
        i === 0
          ? distanceRotation
          : this.getPseudoRandomRotation(visibleCard.title)
      const translateX = i === 0 ? x : 0
      const translateY = i === 0 ? y : 0

      const preset = dragStartTime ? presets.stiff : presets.gentle

      return {
        data: {
          card: visibleCard,
        },
        key: 'card-' + visibleCard.title,
        style: {
          opacity: spring(opacity, preset),
          rotation: spring(rotation, preset),
          translateX: spring(translateX, preset),
          translateY: spring(translateY, preset),
        },
      }
    })
  }

  private willEnterHandler = (styledCard: TransitionStyle) => {
    return {
      opacity: 0,
      rotation: Math.floor((Math.random() - 0.5) * CARD_ROTATION_AMOUNT),
      translateX: Math.floor((Math.random() - 0.5) * CARD_MOVEMENT_DISTANCE),
      translateY: Math.floor((Math.random() - 0.5) * CARD_MOVEMENT_DISTANCE),
    }
  }

  private willLeaveHandler = (styledCard: TransitionStyle) => {
    return {
      opacity: spring(0, presets.stiff),
      rotation: spring(10, presets.gentle),
      translateX: spring(0, presets.gentle),
      translateY: spring(10, presets.gentle),
    }
  }

  private handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    const targetClasses = (e.target as HTMLDivElement).classList

    if (!targetClasses.contains('card__toucharea')) {
      return
    }

    e.preventDefault()
    this.setState({
      dragStartTime: new Date().getTime(),
      pos: {
        x: e.clientX,
        y: e.clientY,
      },
      startPos: {
        x: e.clientX,
        y: e.clientY,
      },
    })
  }

  private handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    const targetClasses = (e.target as HTMLDivElement).classList

    if (!targetClasses.contains('card__toucharea')) {
      return
    }

    e.preventDefault()
    const touch = e.targetTouches[0]
    this.setState({
      dragStartTime: new Date().getTime(),
      pos: {
        x: touch.clientX,
        y: touch.clientY,
      },
      startPos: {
        x: touch.clientX,
        y: touch.clientY,
      },
    })
  }

  private handleTouchMove = (e: TouchEvent) => {
    if (!this.state.dragStartTime) {
      return
    }

    e.preventDefault()

    const touch = e.targetTouches[0]

    this.setState({
      pos: {
        x: touch.clientX,
        y: touch.clientY,
      },
    })
  }

  private handleMouseMove = (e: MouseEvent) => {
    if (!this.state.dragStartTime) {
      return
    }

    e.preventDefault()

    this.setState({
      pos: {
        x: e.clientX,
        y: e.clientY,
      },
    })
  }

  private handleTouchEnd = (e: TouchEvent) => {
    if (!this.state.dragStartTime) {
      return
    }

    e.preventDefault()
    this.endPanHandler()
  }

  private handleMouseUp = (e: MouseEvent) => {
    if (!this.state.dragStartTime) {
      return
    }

    e.preventDefault()
    this.endPanHandler()
  }

  private endPanHandler = () => {
    const { dragStartTime, pos, startPos } = this.state
    const { store } = this.injected

    const delta = {
      x: pos.x - startPos.x,
      y: pos.y - startPos.y,
    }

    const endTime = new Date().getTime()

    const vel = {
      x: Math.abs(delta.x) / (endTime - dragStartTime),
      y: Math.abs(delta.y) / (endTime - dragStartTime),
    }

    const magnitude = Math.sqrt(vel.x * vel.x + vel.y * vel.y)

    if (magnitude > 0.1) {
      store.completeEvent()
    }

    this.setState({
      dragStartTime: 0,
      pos: { x: 0, y: 0 },
      startPos: { x: 0, y: 0 },
    }, () => {
      if (this) {
        this.forceUpdate() 
      }
    })
  }

  private handlePrevClick = () => {
    const { store } = this.injected

    store.undoCompleteEvent()
  }

  private handleNextClick = () => {
    const { store } = this.injected

    store.completeEvent()
  }

  private handleEndClick = () => {
    const { store } = this.injected

    store.player.reset()
  }
}

export default CardStack



// WEBPACK FOOTER //
// ./src/components/CardStack.tsx